import React, { useState } from 'react';
import { Menu, X, TrendingUp, ChevronDown, Zap } from 'lucide-react';
import { useNavigate, NavLink } from 'react-router-dom';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const navigate = useNavigate();

  const handleDropdownToggle = (e, itemId) => {
    e.stopPropagation();
    setOpenDropdown(openDropdown === itemId ? null : itemId);
  };

  const closeAll = () => {
    setIsMenuOpen(false);
    setOpenDropdown(null);
  };

  const navItems = [
    { id: 'home', label: 'Home', path: '/' },
    { id: 'about', label: 'About', path: '/aboutus' },
    {
      id: 'services',
      label: 'Our Services',
      dropdown: [
        { id: 'stock-cash', label: 'Stock Cash Delivery', path: '/services/stock-cash' },
        { id: 'options', label: 'Stock Options', path: '/services/options' },
        { id: 'future', label: 'Stock Future', path: '/services/future' },
        { id: 'banknifty-options', label: 'Banknifty / Nifty / Sensex / Options', path: '/services/banknifty-options' },
        { id: 'banknifty-futures', label: 'Banknifty / Nifty Futures', path: '/services/banknifty-futures' },
        { id: 'commodities', label: 'Commodities', path: '/services/commodity' },
        { id: 'equity-research', label: 'Equity Research', path: '/equity-research' },
        { id: 'financial-adviser', label: 'Financial Adviser', path: '/financial-adviser' },
      ]
    },
    { 
      id: 'research-report', 
      label: 'Research Report',
      path: '/research-report'
    },
    {
      id: 'more',
      label: 'More',
      dropdown: [
        { id: 'blog', label: 'Blog', path: '/blog' },
        { id: 'privacy-policy', label: 'Privacy Policy', path: '/privacy-policy' },
        { id: 'terms-and-conditions', label: 'Terms and Conditions', path: '/terms-and-conditions' },
        { id: 'refund-policy', label: 'Refund Policy', path: '/refund_policy' },
        { id: 'disclaimer', label: 'Disclaimer', path: '/disclaimer' },
        { id: 'disclosure', label: 'Disclosure', path: '/disclosure' },
        { id: 'service-disclosure', label: 'Service Disclosure', path: '/service-disclosure' },
        { id: 'client-kyc-form', label: 'Client Consent', path: '/client-consent' },
        { id: 'complaints', label: 'Complaints', path: '/complaints' },
        { id: 'complaints-redressal', label: 'Complaints Redressal', path: '/complaints-redressal' },
        { id: 'grievance-redressal', label: 'Grievance Redressal', path: '/grievance-redressal' },
        { id: 'faq', label: 'FAQ', path: '/faq' },
        { id: 'testimonials', label: 'Testimonials', path: '/client-testimonials' },
        { id: 'careers', label: 'Careers', path: '/careers' },

        // { id: 'partnership', label: 'Partnership', path: '/partnership' },
        // { id: 'media', label: 'Media Coverage', path: '/media' },
      ],
    },
    { id: 'contact', label: 'Contact', path: '/contactus' },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-lg">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <NavLink
            to="/"
            className="flex items-center space-x-2 cursor-pointer"
            onClick={closeAll}
          >
            <div className="h-16 flex items-center overflow-hidden">
              <img
                src="/logo3.png"
                alt="Omkar Analytics Logo"
                width="96"
                height="64"
                className="h-16 w-auto object-contain transform scale-150"
              />
            </div>

          </NavLink>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              item.dropdown ? (
                <div
                  key={item.id}
                  className="relative group"
                  onMouseEnter={(e) => handleDropdownToggle(e, item.id)}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <button
                    onClick={(e) => handleDropdownToggle(e, item.id)}
                    className={
                      `relative font-medium transition-all duration-300 px-1 flex items-center text-gray-800
                      hover:text-transparent bg-clip-text hover:bg-gradient-to-r hover:from-primary-600 hover:to-secondary-700
                      hover:scale-110 hover:drop-shadow-lg
                      before:absolute before:-bottom-1 before:left-0 before:w-0 before:h-0.5 before:bg-gradient-to-r before:from-primary-600 before:to-secondary-700 before:rounded-full before:transition-all before:duration-300
                      hover:before:w-full`
                    }
                  >
                    {item.label}
                    <ChevronDown className="w-4 h-4 ml-1" />
                  </button>
                  <div className={`absolute left-0 mt-2 w-64 bg-white rounded-lg shadow-lg transition-all duration-200 z-50
                    ${openDropdown === item.id ? 'opacity-100 visible' : 'opacity-0 invisible group-hover:opacity-100 group-hover:visible'}
                  `}>
                    {item.dropdown.map((option) => (
                      <NavLink
                        key={option.id}
                        to={option.path}
                        onClick={closeAll}
                        className={({ isActive }) =>
                          `block w-full text-left px-4 py-2 hover:bg-gradient-to-r hover:from-primary-600 hover:to-secondary-700 hover:text-white rounded-lg transition-all duration-200 first:rounded-t-lg last:rounded-b-lg
                          ${isActive ? 'bg-gradient-to-r from-primary-600 to-secondary-700 text-white' : 'text-gray-800'}`
                        }
                      >
                        {option.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
              ) : (
                <NavLink
                  key={item.id}
                  to={item.path}
                  onClick={closeAll}
                  className={({ isActive }) =>
                    `relative font-medium transition-all duration-300 px-1
                    ${isActive
                      ? 'text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-secondary-700 scale-110 drop-shadow-lg'
                      : 'text-gray-800'
                    }
                    hover:text-transparent bg-clip-text hover:bg-gradient-to-r hover:from-primary-600 hover:to-secondary-700
                    hover:scale-110 hover:drop-shadow-lg
                    before:absolute before:-bottom-1 before:left-0 before:w-0 before:h-0.5 before:bg-gradient-to-r before:from-primary-600 before:to-secondary-700 before:rounded-full before:transition-all before:duration-300
                    hover:before:w-full ${isActive ? 'before:w-full' : ''}`
                  }
                >
                  {item.label}
                </NavLink>
              )
            ))}
          </nav>

          {/* CTA Button and Call Now */}
          <div className="hidden md:flex items-center space-x-4">
            <a
              href="tel:+919898001671"
              className="text-sm font-semibold text-gray-800 hover:text-primary-600 transition-colors animate-blink-hard"
              aria-label="Call Now 9898001671"
            >
              Call Now : 9898001671
            </a>
            <NavLink
              to="/checkout"
              onClick={closeAll}
              className="bg-gradient-to-r from-primary-600 to-secondary-700 hover:from-primary-700 hover:to-secondary-800 text-white px-5 py-2 rounded-lg font-medium text-sm transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg flex items-center space-x-1.5"
            >
              <Zap className="w-3.5 h-3.5 text-yellow-300" />
              <span>Subscribe</span>
            </NavLink>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden transition-colors text-gray-800 hover:text-primary-600"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-gray-200 max-h-[calc(100vh-100px)] overflow-y-auto">
            <nav className="flex flex-col space-y-2 pt-4">
              {navItems.map((item) => (
                item.dropdown ? (
                  <div key={item.id} className="flex flex-col">
                    <button
                      onClick={() => setOpenDropdown(openDropdown === `mobile-${item.id}` ? null : `mobile-${item.id}`)}
                      className={
                        `relative font-medium transition-all duration-300 text-left px-1 flex items-center text-gray-800
                        hover:text-transparent bg-clip-text hover:bg-gradient-to-r hover:from-primary-600 hover:to-secondary-700
                        hover:scale-105 hover:drop-shadow-lg
                        before:absolute before:-bottom-1 before:left-0 before:w-0 before:h-0.5 before:bg-gradient-to-r before:from-primary-600 before:to-secondary-700 before:rounded-full before:transition-all before:duration-300
                        hover:before:w-full`
                      }
                    >
                      {item.label}
                      <ChevronDown className="w-4 h-4 ml-1" />
                    </button>
                    <div className={`ml-4 flex flex-col mt-1 transition-all duration-200 ${openDropdown === `mobile-${item.id}` ? 'max-h-96 overflow-y-auto opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
                      }`}>
                      {item.dropdown.map((option) => (
                        <NavLink
                          key={option.id}
                          to={option.path}
                          onClick={closeAll}
                          className={({ isActive }) =>
                            `block w-full text-left px-4 py-3 text-sm hover:bg-gradient-to-r hover:from-primary-600 hover:to-secondary-700 hover:text-white rounded-lg transition-all duration-200
                            ${isActive ? 'bg-gradient-to-r from-primary-600 to-secondary-700 text-white' : 'text-gray-700'}`
                          }
                        >
                          {option.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                ) : (
                  <NavLink
                    key={item.id}
                    to={item.path}
                    onClick={closeAll}
                    className={({ isActive }) =>
                      `relative font-medium transition-all duration-300 text-left px-1
                      ${isActive
                        ? 'text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-secondary-700 scale-105 drop-shadow-lg'
                        : 'text-gray-800'
                      }
                      hover:text-transparent bg-clip-text hover:bg-gradient-to-r hover:from-primary-600 hover:to-secondary-700
                      hover:scale-105 hover:drop-shadow-lg
                      before:absolute before:-bottom-1 before:left-0 before:w-0 before:h-0.5 before:bg-gradient-to-r before:from-primary-600 before:to-secondary-700 before:rounded-full before:transition-all before:duration-300
                      hover:before:w-full ${isActive ? 'before:w-full' : ''}`
                    }
                  >
                    {item.label}
                  </NavLink>
                )
              ))}
              <a
                href="tel:+919898001671"
                onClick={closeAll}
                className="text-left text-primary-700 font-semibold px-1 animate-blink-hard"
                aria-label="Call Now 9898001671"
              >
                Call Now: 9898001671
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;